import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { IChecklistBenchmarkViewData } from '../checklists-benchmark/checklists-benchmark.component';
import {
    IChecklist2ReportBenchDataByCheckpointGroups,
    IChecklist2ReportDateRange,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { ObjectTypes } from '@fi-shared/types';

interface IChecklistHistoryCheckpointGroupsBenchmarkViewData {
    ChecklistName: string;
    ChecklistId: number;
    CheckpointGroups: IChecklistBenchmarkViewData[];
}

@Component({
    selector: 'fi-checklist-history-checkpoint-groups-benchmark',
    templateUrl: './checkpoint-groups-benchmark.component.html',
    styleUrls: ['./checkpoint-groups-benchmark.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-history-checkpoint-groups-benchmark' },
    standalone: false,
})
export class FiChecklistHistoryCheckpointGroupsBenchmarkComponent implements OnChanges {
    @Input({ required: true }) checklistHistoryDateRange: IChecklist2ReportDateRange;
    @Input({ required: true }) benchmarkFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) auditMode: boolean;
    @Output() onShowDetailedAnswersReport = new EventEmitter<IChecklistV2ReportParameterFilter>();

    benchmarkViewData: IChecklistHistoryCheckpointGroupsBenchmarkViewData[] = [];

    constructor(
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
        private cdr: ChangeDetectorRef,
        private fiChecklist2ReportHistoryHelperService: FiChecklist2ReportHelperService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.checklistHistoryDateRange || changes.benchmarkFilter) this.loadBenchDataByCheckpointGroups();
    }

    private loadBenchDataByCheckpointGroups() {
        this.fiChecklist2ReportHistoryService
            .getBenchDataByCheckpointGroups(this.benchmarkFilter, this.checklistHistoryDateRange, this.auditMode)
            .then((result: IChecklist2ReportBenchDataByCheckpointGroups[]) => {
                this.benchmarkViewData = this.getBenchmarkViewData(result);
                this.cdr.markForCheck();
            });
    }

    getBenchmarkViewData(
        data: IChecklist2ReportBenchDataByCheckpointGroups[],
    ): IChecklistHistoryCheckpointGroupsBenchmarkViewData[] {
        return data.map(
            (
                item: IChecklist2ReportBenchDataByCheckpointGroups,
            ): IChecklistHistoryCheckpointGroupsBenchmarkViewData => {
                return {
                    ChecklistName: item.ChecklistName,
                    ChecklistId: item.ChecklistId,
                    CheckpointGroups: item.CheckpointGroups.map((cpg) => {
                        return {
                            ObjectName: cpg.CheckpointGroupName,
                            ScheduleName: cpg.ScheduleName,
                            ObjectId: cpg.CheckpointGroupId,
                            ScheduleId: cpg.ScheduleId,
                            CreatedByUserId: cpg.CheckpointGroupCreatedByUserId,
                            CreatedByUserName: cpg.CheckpointGroupCreatedByUserName,
                            DueDateTime: cpg.DueDateTime,
                            Units: cpg.Units,
                            CheckpointsCount: cpg.CheckpointsCount,
                        };
                    }),
                };
            },
        );
    }

    getCheckpointCardHeading(item: IChecklistHistoryCheckpointGroupsBenchmarkViewData): string {
        return item.CheckpointGroups?.length
            ? `${item.ChecklistName} - ${item.CheckpointGroups[0].ScheduleName}:`
            : item.ChecklistName;
    }

    getChecklistBenchmarkCardSizeClassName(): string {
        return this.fiChecklist2ReportHistoryHelperService.getChecklistBenchmarkCardSizeClassName(
            this.benchmarkFilter?.BusinessUnitIds?.length,
        );
    }

    showDetailedAnswersReport(
        businessUnitId: number,
        checklistId: number,
        checkpointData: IChecklistBenchmarkViewData,
    ) {
        this.onShowDetailedAnswersReport.emit({
            BusinessUnitIds: [businessUnitId],
            ChecklistIds: [checklistId],
            ScheduleIds: [checkpointData.ScheduleId],
            CheckpointGroupIds: [checkpointData.ObjectId],
        } as IChecklistV2ReportParameterFilter);
    }

    protected readonly ObjectTypes = ObjectTypes;
}
